import { useState } from 'react'
import { useEffect } from 'react'
import { Loading } from './Loading'
import { FullSuccess } from './FullSuccess'
import { PartialSuccess } from './PartialSuccess'
import { Failure } from './Failure'
import { TerminalStatus, TerminalStatusContext } from '../../../types/mf-execution.types'
import { Awaiting } from './Awaiting'
import { updateAppStatusBar } from '../../../utils/appBridge'
import { Pending } from './Pending'
import { getStatusBarColor, getStatusBarTheme } from '../../../utils/sessionStorage'

type LoadingToFullSuccessProps = {
  status: TerminalStatus
  statusContext?: TerminalStatusContext
  loadingConfig?: {
    title: string
    caption?: string
    titleClassName?: string
  }
  fullSuccessConfig?: {
    title: string
    caption?: string
    ctas: {
      ctaType: 'primary' | 'secondary'
      label: string
      onClick: () => void
    }[]
  }
  partialSuccessConfig?: {
    title: string
    caption?: string
    ctas?: {
      ctaType: 'primary' | 'secondary'
      label: string
      onClick: () => void
    }[]
  }
  awaitingConfig?: {
    title: string
    subtitle?: string
    timerText: string
    caption?: string
    onBackButtonClick?: () => void
  }
  failureConfig?: {
    title: string
    caption?: string
    ctas: {
      ctaType: 'primary' | 'secondary'
      label: string
      onClick: () => void
    }[]
  }
  pendingConfig?: {
    title: string
    caption?: string
    ctas?: {
      ctaType: 'primary' | 'secondary'
      label: string
      onClick: () => void
    }[]
  }
}

export const AllStatusWrapper = ({
  status,
  statusContext,
  loadingConfig,
  fullSuccessConfig,
  partialSuccessConfig,
  failureConfig,
  pendingConfig,
  awaitingConfig
}: LoadingToFullSuccessProps) => {
  const [localStatus, setLocalStatus] = useState(status)
  const [isTransitioning, setIsTransitioning] = useState(false) // this is used to transition from "only" loading to full success

  // when status goes from loading -> full success, transition should occur
  // also when status goes from partial success -> full success, transition should occur
  useEffect(() => {
    if (
      status === TerminalStatus.FULL_SUCCESS &&
      [TerminalStatus.LOADING, TerminalStatus.AWAITING,
      TerminalStatus.PARTIAL_SUCCESS].includes(localStatus)
    ) {
      setIsTransitioning(true)
      setTimeout(() => {
        setLocalStatus(status)
        setIsTransitioning(false)
      }, 400)
    } else {
      setLocalStatus(status)
    }
  }, [status])

  useEffect(() => {
    if (localStatus === TerminalStatus.FULL_SUCCESS) {
      updateAppStatusBar('136FCC', 'light')
    } else {
      updateAppStatusBar('FFFFFF', 'dark')
    }
    return () => {
      updateAppStatusBar(getStatusBarColor() ?? 'FFFFFF', getStatusBarTheme() ?? 'dark')
    }
  }, [localStatus])

  const getTerminalComponent = (statusParam: TerminalStatus) => {
    switch (statusParam) {
      case TerminalStatus.FULL_SUCCESS:
        return (
          <FullSuccess
            title={fullSuccessConfig?.title ?? ''}
            caption={fullSuccessConfig?.caption}
            ctas={fullSuccessConfig?.ctas ?? []}
            statusContext={statusContext}
          />
        )
      case TerminalStatus.PARTIAL_SUCCESS:
        return (
          <PartialSuccess
            title={partialSuccessConfig?.title ?? ''}
            caption={partialSuccessConfig?.caption}
            ctas={partialSuccessConfig?.ctas ?? []}
            statusContext={statusContext}
          />
        )
      case TerminalStatus.FAILURE:
        return (
          <Failure
            title={failureConfig?.title ?? ''}
            caption={failureConfig?.caption}
            ctas={failureConfig?.ctas ?? []}
            statusContext={statusContext}
          />
        )
      case TerminalStatus.PENDING:
        return (
          <Pending
            title={pendingConfig?.title ?? ''}
            caption={pendingConfig?.caption}
            ctas={pendingConfig?.ctas ?? []}
            statusContext={statusContext}
          />
        )
      case TerminalStatus.LOADING:
        return (
          <Loading
            title={loadingConfig?.title ?? ''}
            caption={loadingConfig?.caption}
            titleClassName={loadingConfig?.titleClassName}
            statusContext={statusContext}
          />
        )
      case TerminalStatus.AWAITING:
        return (
          <Awaiting
            title={awaitingConfig?.title ?? ''}
            subtitle={awaitingConfig?.subtitle ?? ''}
            timerText={awaitingConfig?.timerText ?? ''}
            caption={awaitingConfig?.caption}
            onBackButtonClick={awaitingConfig?.onBackButtonClick}
            statusContext={statusContext}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='w-full h-screen relative overflow-hidden'>
      {getTerminalComponent(localStatus)}

      {isTransitioning && (
        <div className='absolute top-1/2 left-0 flex items-center justify-center pointer-events-none'>
          <div className='w-3 h-3 bg-[#136FCC] rounded-full animate-smooth-expand' />
        </div>
      )}

      <style>
        {`
          @keyframes smoothExpand {
            0% {
              transform: scale(0);
              opacity: 1;
            }
            100% {
              transform: scale(200);
              opacity: 1;
              background-color: #136FCC;
            }
          }
          
          .animate-smooth-expand {
            animation: smoothExpand 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }
        `}
      </style>
    </div>
  )
}
